import { YesObject } from '@yes.technology/react-toolkit'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import { SelectAndLinkOptions } from 'site/Renderer/components/DataTables/types'
import { useSelectAndLink } from 'site/Renderer/components/SelectAndLink'
import { targetComponents } from 'site/Renderer/components/SelectAndLink/SelectAndLink.constants'
import { useFetchYesObjects } from 'yesObject/shared'
import getRelationCounterpartField from '../../lib/getRelationCountertpartField/getRelationCounterpartField'
import { relationObjectsAtColumn } from '../../lib/relationObjectsAtColumn'
import RelationAutocomplete from '../RelationAutocomplete/RelationAutocomplete'
import { RelationClassProps } from './RelationClass.types'
import RelationClassTable from './RelationClassTable'
import useRelationClassCardAutoEditEffect from './useRelationClassCardAutoEditEffect'
import useRelationClassLocalRelationState from './useRelationClassLocalRelationState'
import useRelationClassRelationSave from './useRelationClassRelationSave'
import useRelationClassRelationUnlink from './useRelationClassRelationUnlink'
import useRelationClassSelectAndLinkFieldSync from './useRelationClassSelectAndLinkFieldSync'
import NestedRelationCard from '../NestedRelationCard/NestedRelationCard'

const RELATION_TABLE_PAGE_SIZE = 100

const RelationClass = ({
  fieldType = 'table',
  sourcesConfig = {},
  relationFieldRelation,
  fieldValues,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  field,
  informational,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  fieldSettings,
  hideInsertAction = false,
  hideSelectAndLinkAction = false,
  hideDocumentUploadAction = true,
  selectAndLinkTargetPath,
  uploadAcceptedFileTypes,
  isEditing = false,
  edit,
  onChange,
  changeAndSave,
  serverFieldValues
}: RelationClassProps) => {
  const objectclassUuid = relationFieldRelation?.uuid_objectclass || ''

  const relationCounterpartField = getRelationCounterpartField({
    relationFieldRelation,
    fieldValues
  })

  const [relationCounterpartColumn] = Object.keys(
    relationCounterpartField || {}
  )

  const {
    yesObjects,
    isFetching,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects: refetchObjects
  } = useFetchYesObjects({
    initialPagination: { limit: RELATION_TABLE_PAGE_SIZE, offset: 0 },
    filterBody: {
      query: {
        $and: [
          {
            [`${relationCounterpartColumn}.uuid`]: {
              $in: [`${fieldValues?.uuid}`]
            },
            'situation.des': {
              $gte: globalThis.activeSituationCode
            }
          }
        ]
      },
      objectclass: objectclassUuid,
      data_storage: ''
    },
    enabled: !!relationCounterpartColumn && !!objectclassUuid
  })

  const { getSession, onClearSelection, removeSelectedOptionByUuid, sessions } =
    useSelectAndLink()

  const currentField = field?.column_json || ''
  const isUniselectionRelation =
    relationFieldRelation?.multiplicity === '1:1' ||
    relationFieldRelation?.multiplicity === 'N:1'

  const { selectAndLinkSelectedOptions } = useMemo(() => {
    const currentFieldSession =
      sessions[currentField] ?? getSession(currentField)
    const selectAndLinkSelectedOptions =
      currentFieldSession?.isCompleted &&
      Array.isArray(currentFieldSession.selectedOptions)
        ? currentFieldSession.selectedOptions
        : undefined
    return { currentFieldSession, selectAndLinkSelectedOptions }
  }, [currentField, sessions, getSession])

  const currentRelationFromParent = useMemo(
    () => relationObjectsAtColumn(fieldValues, currentField),
    [fieldValues, currentField]
  )

  const id = useId()

  const relationId = currentField || id

  const tableId = currentField || id

  const onScrollToRelationTable = useCallback(() => {
    const relationTable = document.getElementById(relationId)

    if (relationTable) {
      relationTable.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [relationId])

  const {
    unsavedObjects,
    setUnsavedObjects,
    savedObjectsToRemove,
    setSavedObjectsToRemove,
    relationWorkingSet
  } = useRelationClassLocalRelationState(currentRelationFromParent, isEditing)

  useRelationClassCardAutoEditEffect({
    fieldType,
    isEditing,
    isFetching,
    unsavedObjects,
    yesObjects,
    edit
  })

  useRelationClassSelectAndLinkFieldSync({
    isUniselectionRelation,
    currentField,
    selectAndLinkSelectedOptions,
    currentRelationFromParent,
    unsavedObjects,
    setUnsavedObjects,
    setSavedObjectsToRemove,
    serverFieldValues,
    yesObjects,
    onChange,
    isFetching,
    onScrollToRelationTable
  })

  const prevIsEditingRef = useRef(isEditing)
  useEffect(() => {
    const wasEditing = prevIsEditingRef.current
    prevIsEditingRef.current = isEditing
    if (wasEditing && !isEditing && getSession(currentField)) {
      onClearSelection(currentField)
    }
  }, [isEditing, currentField, getSession, onClearSelection])

  const handleSave = useRelationClassRelationSave({
    isUniselectionRelation,
    selectAndLinkSelectedOptions,
    changeAndSave,
    currentField,
    relationWorkingSet,
    refetchObjects,
    relationCounterpartColumn,
    fieldValues,
    objectclassUuid,
    onScrollToRelationTable
  })

  const unlinkObjects = useRelationClassRelationUnlink({
    currentField,
    serverFieldValues,
    unsavedObjects,
    relationWorkingSet,
    changeAndSave,
    onChange
  })

  const tableObjects = useMemo(
    () => [
      ...yesObjects.filter(
        (object) =>
          !savedObjectsToRemove.some(
            (objectToRemove) => objectToRemove.uuid === object.uuid
          )
      ),
      ...unsavedObjects
    ],
    [yesObjects, unsavedObjects, savedObjectsToRemove]
  )

  const title = field?.display_text?.des || ''

  const selectAndLinkModalSelectedOptions = useMemo(
    () => uniqBy(tableObjects, 'uuid'),
    [tableObjects]
  )

  const selectAndLinkOptions: SelectAndLinkOptions = useMemo(
    () => ({
      targetComponent: targetComponents.OBJECT_VIEW,
      targetField: currentField,
      selectedOptions: selectAndLinkModalSelectedOptions,
      uniselection: fieldType === 'card' ? isUniselectionRelation : undefined
    }),
    [
      currentField,
      selectAndLinkModalSelectedOptions,
      fieldType,
      isUniselectionRelation
    ]
  )

  if (fieldType === 'simple') {
    return (
      <NestedRelationCard
        object={tableObjects[0]}
        id={tableId}
        {...{
          objectclassUuid,
          sourcesConfig,
          fieldSettings,
          layoutClassificationUuid,
          title,
          navigationSliderBackgroundColor,
          navigationSliderColor
        }}
      />
    )
  }

  if (fieldType === 'card') {
    const handleAutocompleteChange = (options: YesObject[]) => {
      const getNormalizedOptions = (): YesObject[] => {
        if (!isUniselectionRelation) {
          return options
        }

        const lastOption = options.at(-1)

        if (lastOption === undefined) {
          return []
        }

        return [lastOption]
      }

      const normalizedOptions = getNormalizedOptions()
      const nextUuids = new Set(normalizedOptions.map((o) => o.uuid))
      for (const object of tableObjects) {
        if (!nextUuids.has(object.uuid)) {
          removeSelectedOptionByUuid(object.uuid, currentField)
        }
      }

      onChange(currentField, normalizedOptions)

      const newSelectedOptions = normalizedOptions.filter(
        (option) => !tableObjects.find((object) => object.uuid === option.uuid)
      )
      setUnsavedObjects((oldUnsavedObjects) => [
        ...oldUnsavedObjects.filter((object) =>
          normalizedOptions.find((option) => option.uuid === object.uuid)
        ),
        ...newSelectedOptions
      ])

      setSavedObjectsToRemove(
        yesObjects.filter(
          (object) =>
            !normalizedOptions.some((option) => option.uuid === object.uuid)
        )
      )
    }

    return (
      <RelationAutocomplete
        {...{
          objectclassUuid,
          selectAndLinkOptions,
          selectAndLinkTargetPath,
          hideSelectAndLinkAction,
          title,
          navigationSliderBackgroundColor,
          navigationSliderColor
        }}
        id={tableId}
        multiplicity={relationFieldRelation?.multiplicity || 'N:N'}
        onChange={handleAutocompleteChange}
        selectedObjects={tableObjects}
        informational={informational || !isEditing}
      />
    )
  }

  return (
    <RelationClassTable
      onSave={handleSave}
      onUnlink={unlinkObjects}
      {...{
        title,
        informational,
        objectclassUuid,
        tableObjects,
        currentField,
        fieldSettings,
        tableId,
        layoutClassificationUuid,
        isFetching,
        hierarchyLevel,
        actionButton,
        hideInsertAction,
        hideSelectAndLinkAction,
        hideDocumentUploadAction,
        selectAndLinkTargetPath,
        id: tableId,
        uploadAcceptedFileTypes,
        selectAndLinkOptions,
        pagination,
        sortOptions,
        totalObjects,
        unsavedObjects,
        setSortOptions,
        setPage,
        refetchObjects,
        navigationSliderBackgroundColor,
        navigationSliderColor
      }}
    />
  )
}

export default RelationClass
